import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  HeartHandshake, 
  Users, 
  Pill, 
  ShoppingBag, 
  Car, 
  Hospital,
  Sparkles,
  Dog,
  Coffee,
  Smartphone,
  ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { Phone } from 'lucide-react';
import careKitchen from '../assets/care-kitchen.png';
import careCleaning1 from '../assets/care-cleaning1.png';
import careTea from '../assets/care-tea.png';
import careShopping from '../assets/care-shopping.png';
import careTransport from '../assets/care-transport.png';
import careWalking from '../assets/care-walking.png';
import careTablet from '../assets/care-tablet.png';
import careCleaning2 from '../assets/care-cleaning2.png';

const carouselImages = [
  { src: careTea,       alt: 'Care worker having tea with elderly client' },
  { src: careShopping,  alt: 'Care worker helping with shopping' },
  { src: careTransport, alt: 'Care worker helping elderly client with transport' },
  { src: careWalking,   alt: 'Care worker walking with elderly client in park' },
  { src: careTablet,    alt: 'Care worker helping elderly client with tablet' },
  { src: careCleaning1, alt: 'Care worker vacuuming for elderly client' },
  { src: careKitchen,   alt: 'Care worker cooking with elderly client' },
  { src: careCleaning2, alt: 'Care worker tidying home for elderly client' },
];

const ServicesSection = () => {
  const [current, setCurrent] = useState(0);
  const total = carouselImages.length;

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % total);
  }, [total]);

  const prev = () => {
    setCurrent(prev => (prev - 1 + total) % total);
  };

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  const services = [
    {
      icon: HeartHandshake,
      title: 'Welfare & Safety Check Visits',
      description: 'Regular home visits to check clients are safe, comfortable and managing well at home.',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: Users,
      title: 'Companionship Visits',
      description: 'Friendly visits providing conversation, emotional support and social interaction.',
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: Pill,
      title: 'Medication Reminder Support',
      description: 'Gentle prompts to help clients take medication on time (no administration).',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: ShoppingBag,
      title: 'Shopping & Errands Assistance',
      description: 'Help with groceries, prescription collection, and essential daily tasks.',
      color: 'from-cyan-600 to-teal-600'
    },
    {
      icon: Car,
      title: 'Appointment Transport Support',
      description: 'Reliable transport to GP, hospital, and community appointments.',
      color: 'from-teal-600 to-emerald-600'
    },
    {
      icon: Hospital,
      title: 'Hospital Discharge Support',
      description: 'Support settling safely back home after hospital stays.',
      color: 'from-emerald-600 to-cyan-600'
    },
    {
      icon: Sparkles,
      title: 'Home Cleaning & Household Support',
      description: 'Light cleaning and help maintaining a safe, tidy home.',
      color: 'from-cyan-500 to-teal-500'
    },
    {
      icon: Dog,
      title: 'Pet Walking & Pet Sitting',
      description: 'Support caring for pets, including feeding and walking.',
      color: 'from-teal-500 to-emerald-500'
    },
    {
      icon: Coffee,
      title: 'Social Visits & Community Support',
      description: 'Helping clients stay active and engaged in their community.',
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: Smartphone,
      title: 'Technology & Digital Support',
      description: 'Help using phones, tablets, and staying connected.',
      color: 'from-cyan-600 to-teal-600'
    },
    {
      icon: ShieldCheck,
      title: 'Home Safety & Organisation Support',
      description: 'Assistance identifying risks and improving home safety.',
      color: 'from-teal-600 to-emerald-600'
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider"></span>
          <h2 className="text-4xl font-bold text-slate-900 mt-2 mb-4">
            Our Services
          </h2>
          <p className="text-lg text-slate-600">
            We offer flexible, non-personal care support tailored to individual needs.
          </p>
        </div>

        {/* Services Grid - 4 per row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-teal-300 bg-white overflow-hidden"
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-teal-700 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Services CTA */}
        <div className="text-center mt-12 p-8 bg-teal-50 rounded-2xl border border-teal-100">
          <p className="text-lg text-slate-700 font-medium mb-4">
            Need support for yourself or a loved one? Call us today on{' '}
            <span className="text-teal-600 font-bold">07475 473 449</span>
          </p>
          <a href="tel:07475473449">
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-10 py-6 text-lg">
              <Phone className="mr-2 w-5 h-5" />
              Get in Touch
            </Button>
          </a>
        </div>

        {/* Image Carousel */}
        <div className="mt-16 relative rounded-2xl overflow-hidden shadow-2xl">
          <div className="relative h-[400px] md:h-[500px] lg:h-[600px] w-full">
            {carouselImages.map((image, index) => (
              <img
                key={index}
                src={image.src}
                alt={image.alt}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                  index === current ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>

          {/* Left Arrow */}
          <button
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
          >
            <ChevronLeft className="w-6 h-6 text-slate-700" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
          >
            <ChevronRight className="w-6 h-6 text-slate-700" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrent(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                  index === current
                    ? 'bg-white scale-125'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;